/**
 * Funnel Configurator Widget - Standalone Bundle
 * Renders the FULL configurator directly in the DOM (no iframe)
 */
import React, { useEffect, useState } from 'react'

declare const jQuery: any
import { createRoot } from 'react-dom/client'
import { Configurator } from '@/components/Configurator'
import { Swiper } from '@/components/Swiper'
import { Bundle, BundleCartPayload } from '@/components/Bundle'
import { ConfigProduct, setWidgetApiBaseUrl, useFunnelConfig } from '@/lib/hooks/useFunnelConfig'
// Import CSS as string for Shadow DOM injection
import widgetStyles from './widget.css?inline'
import { mountBundleWizard } from './bundle-wizard/mount'
import { LotUpsellSlot, LotUpsellHintSlot } from './lot-upsell/LotUpsell'
import { ct, setConfiguratorLang } from '@/lib/i18n/configurator'

// Langue de la page (TrueLang pose <html lang="nl">…) : le configurateur s'affiche dans cette langue, sinon en français
setConfiguratorLang()

// Default API URL for production widget
const DEFAULT_API_URL = 'https://funnelconfig1.vercel.app'

interface InitOptions {
  container: HTMLElement | string
  funnelId: string
  apiUrl?: string
  wcAjaxUrl?: string
  wcNonce?: string
}

// Global namespace
declare global {
  interface Window {
    FunnelConfigurator: {
      init: (options: InitOptions) => void
      initSwiper: (options: InitOptions) => void
      version: string
    }
  }
}

// Envoi vers Google Analytics (gtag) et le pixel Meta (fbq) déjà chargés par le site ; silencieux s'ils sont absents
type Tracker = (...args: unknown[]) => void
function trackConfig(name: string, params: Record<string, unknown>) {
  const w = window as unknown as { gtag?: Tracker; fbq?: Tracker }
  try { w.gtag?.('event', name, params) } catch { /* suivi indisponible */ }
  try { w.fbq?.('trackCustom', name, params) } catch { /* suivi indisponible */ }
}
function trackStandardAddToCart(slug: string, value: number) {
  const w = window as unknown as { gtag?: Tracker; fbq?: Tracker }
  try { w.gtag?.('event', 'add_to_cart', { currency: 'EUR', value, items: [{ item_id: slug, item_name: 'Configurateur', item_category: 'Configurateur', price: value, quantity: 1 }] }) } catch { /* */ }
  try { w.fbq?.('track', 'AddToCart', { currency: 'EUR', value, content_name: 'Configurateur', content_type: 'product', content_ids: [slug] }) } catch { /* */ }
}

// Wrapper component that handles WooCommerce add to cart for both
// configurator and bundle types. Dispatches based on the funnel type.
function WidgetWrapper({
  slug,
  wcAjaxUrl,
  wcNonce
}: {
  slug: string
  wcAjaxUrl?: string
  wcNonce?: string
}) {
  const { config, isLoading } = useFunnelConfig(slug)
  const [cartAdded, setCartAdded] = useState(false)
  const [cartLoading, setCartLoading] = useState(false)
  const [lotOpen, setLotOpen] = useState(false) // panneau « lot −20 % » ouvert depuis le configurateur

  // Configurateur classique (pas le lot) : mesure du parcours « je découvre avec le configurateur, puis je commande »
  // (demande de Theo, 19/10/2026). Repère navigateur 30 jours, recopié sur la commande par le plugin
  // (_fc_configurateur_vu), et événements GA4 / Meta.
  useEffect(() => {
    if (!config || config.type === 'bundle') return
    try {
      if (!/(?:^|;\s*)fc_cfg_seen=/.test(document.cookie)) {
        document.cookie = `fc_cfg_seen=${Math.floor(Date.now() / 1000)}; path=/; max-age=${30 * 86400}; SameSite=Lax`
      }
    } catch { /* cookies indisponibles */ }
    trackConfig('config_ouvert', { configurateur: slug })
  }, [config, slug])

  const handleAddToCart = (products: ConfigProduct[], total: number, baseWcId?: number | null) => {
    const productIds = products.map(p => p.wcId).filter((id): id is number => !!id)
    if (baseWcId) productIds.unshift(baseWcId)

    if (!wcAjaxUrl || !wcNonce || productIds.length === 0) {
      console.log('[FunnelConfigurator] Add to cart:', { productIds, total })
      alert(`${products.length} produits sélectionnés`)
      return
    }

    setCartLoading(true)

    const formData = new FormData()
    formData.append('action', 'fc_add_to_cart')
    formData.append('nonce', wcNonce)
    formData.append('funnel_slug', slug) // marqueur « configurateur » sur les lignes du panier et de la commande
    productIds.forEach(id => formData.append('product_ids[]', String(id)))

    fetch(wcAjaxUrl, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin'
    })
      .then(res => res.json())
      .then(data => {
        setCartLoading(false)
        if (data.success) {
          setCartAdded(true)
          setTimeout(() => setCartAdded(false), 3000)
          const value = Math.round(total) / 100
          trackConfig('config_ajout_panier', { configurateur: slug, valeur: value, produits: productIds.length })
          trackStandardAddToCart(slug, value)
          if (typeof jQuery !== 'undefined') {
            jQuery(document.body).trigger('wc_fragment_refresh')
          }
        } else {
          console.error('[FunnelConfigurator] Add to cart failed:', data)
          alert(ct('cartError'))
        }
      })
      .catch(err => {
        setCartLoading(false)
        console.error('[FunnelConfigurator] Add to cart error:', err)
        alert(ct('cartError'))
      })
  }

  const handleBundleAddToCart = (payload: BundleCartPayload) => {
    if (!wcAjaxUrl || !wcNonce) {
      console.log('[FunnelBundle] Add to cart payload:', payload)
      alert(`Lot ${payload.bundleName}\n${payload.baseWcIds.length} items + ${payload.slotPicks.length} slots`)
      return
    }

    setCartLoading(true)

    const formData = new FormData()
    formData.append('action', 'fc_add_bundle_to_cart')
    formData.append('nonce', wcNonce)
    formData.append('bundle_slug', payload.bundleSlug)
    formData.append('bundle_name', payload.bundleName)
    payload.baseWcIds.forEach(id => formData.append('base_wc_ids[]', String(id)))
    payload.baseOverrides.forEach((override, i) => {
      formData.append(`base_overrides[${i}][category_id]`, override.categoryId)
      formData.append(`base_overrides[${i}][product_wc_id]`, String(override.productWcId))
    })
    payload.slotPicks.forEach((pick, i) => {
      formData.append(`slot_picks[${i}][slot_id]`, pick.slotId)
      formData.append(`slot_picks[${i}][slot_name]`, pick.slotName)
      formData.append(`slot_picks[${i}][product_wc_id]`, String(pick.productWcId))
    })
    payload.extras.forEach((extra, i) => {
      formData.append(`extras[${i}][category_id]`, extra.categoryId)
      formData.append(`extras[${i}][category_name]`, extra.categoryName)
      formData.append(`extras[${i}][product_wc_id]`, String(extra.productWcId))
    })

    fetch(wcAjaxUrl, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin'
    })
      .then(res => res.json())
      .then(data => {
        setCartLoading(false)
        if (data.success) {
          setCartAdded(true)
          setTimeout(() => setCartAdded(false), 3000)
          if (typeof jQuery !== 'undefined') {
            // Trigger the same events as a native WC add-to-cart so the
            // theme's mini-cart + fragments update and open as usual.
            jQuery(document.body).trigger('wc_fragment_refresh')
            jQuery(document.body).trigger('added_to_cart')
          }
        } else {
          console.error('[FunnelBundle] Add to cart failed:', data)
          alert(data.message || 'Erreur lors de l\'ajout du lot au panier')
        }
      })
      .catch(err => {
        setCartLoading(false)
        console.error('[FunnelBundle] Add to cart error:', err)
        alert('Erreur lors de l\'ajout du lot au panier')
      })
  }

  if (isLoading) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#6b7280' }}>Chargement…</div>
  }

  if (config?.type === 'bundle') {
    return <Bundle slug={slug} onAddToCart={handleBundleAddToCart} cartLoading={cartLoading} cartAdded={cartAdded} />
  }

  return (
    <Configurator
      slug={slug}
      onAddToCart={handleAddToCart}
      cartLoading={cartLoading}
      cartAdded={cartAdded}
      renderAfterSteps={ctx => <LotUpsellSlot ctx={ctx} configSlug={slug} wcAjaxUrl={wcAjaxUrl} wcNonce={wcNonce} open={lotOpen} setOpen={setLotOpen} />}
      renderAboveBuy={ctx => <LotUpsellHintSlot ctx={ctx} configSlug={slug} setOpen={setLotOpen} />}
    />
  )
}

function init(options: InitOptions) {
  const {
    container,
    funnelId,
    apiUrl,
    wcAjaxUrl,
    wcNonce,
  } = options

  // Set API base URL for cross-origin requests
  const baseApiUrl = apiUrl || DEFAULT_API_URL
  setWidgetApiBaseUrl(baseApiUrl)

  // Get container element
  const containerEl = typeof container === 'string'
    ? document.querySelector(container)
    : container

  if (!containerEl) {
    console.error('[FunnelConfigurator] Container not found:', container)
    return
  }

  if (!funnelId) {
    console.error('[FunnelConfigurator] funnelId is required')
    return
  }

  const { renderContainer } = createShadowRoot(containerEl)
  liftFloatingButtons(containerEl as HTMLElement)

  const root = createRoot(renderContainer)
  root.render(
    <React.StrictMode>
      <WidgetWrapper
        slug={funnelId}
        wcAjaxUrl={wcAjaxUrl}
        wcNonce={wcNonce}
      />
    </React.StrictMode>
  )
}

/**
 * Boutons flottants du site sur les pages du configurateur (signalé par Theo, 19/10/2026) :
 * - le sélecteur de langue TrueLang passait par-dessus le prix de la barre « Acheter » sur mobile. Il est masqué ici :
 *   la langue choisie ailleurs suit dans l'adresse (/nl/…), et l'interface du configurateur n'est pas traduite ;
 * - la bulle du mini-panier est remontée au-dessus de la barre tant que celle-ci est collée en bas de l'écran.
 */
function liftFloatingButtons(host: HTMLElement) {
  if (!document.getElementById('fc-config-floating')) {
    const st = document.createElement('style')
    st.id = 'fc-config-floating'
    st.textContent = `html body div#truelang-switcher { display: none !important; } .xoo-wsc-basket { bottom: calc(var(--fc-wizard-bottom, 0px) + 12px) !important; transition: bottom .2s ease; }`
    document.head.appendChild(st)
  }
  // Une règle de TrueLang plus spécifique l'emporte sur la feuille : on masque aussi sur l'élément lui-même,
  // et de nouveau si TrueLang le recrée
  const hideSwitcher = () => {
    const sw = document.getElementById('truelang-switcher')
    if (sw && sw.style.getPropertyValue('display') !== 'none') sw.style.setProperty('display', 'none', 'important')
  }
  hideSwitcher()
  new MutationObserver(hideSwitcher).observe(document.body, { childList: true })
  let last = -1
  const update = () => {
    const bar = host.shadowRoot?.querySelector('[data-fc-buybar]') as HTMLElement | null
    let h = 0
    if (bar) {
      const r = bar.getBoundingClientRect()
      // collée en bas : son bas touche celui de l'écran (sur PC, la barre n'est pas collée)
      if (r.height > 0 && r.top < window.innerHeight && r.bottom >= window.innerHeight - 2) h = Math.round(window.innerHeight - r.top)
    }
    if (h !== last) { last = h; document.documentElement.style.setProperty('--fc-wizard-bottom', h + 'px') }
  }
  window.addEventListener('scroll', update, { passive: true })
  window.addEventListener('resize', update)
  setInterval(update, 800) // la barre apparaît après le chargement du configurateur
}

/** Shared Shadow DOM setup for both configurator and swiper */
function createShadowRoot(containerEl: Element) {
  const shadowRoot = (containerEl as HTMLElement).attachShadow({ mode: 'open' })

  const styleEl = document.createElement('style')
  styleEl.textContent = `
    /* Base shadow host setup */
    :host {
      display: block;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    /* Box-sizing reset */
    *, *::before, *::after {
      box-sizing: border-box;
    }

    /* Tailwind compiled CSS */
    ${widgetStyles}

    /* FORCE critical styles with !important to override any Breakdance leaks */
    /* Border widths */
    .border { border-width: 1px !important; border-style: solid !important; }
    .border-2 { border-width: 2px !important; border-style: solid !important; }

    /* Border colors - SELECTED STATE */
    .border-gray-900 { border-color: #111827 !important; border-width: 2px !important; }
    .border-\\[\\#171717\\] { border-color: #171717 !important; }

    /* Border colors - DEFAULT STATE */
    .border-gray-200 { border-color: #e5e7eb !important; }
    .border-gray-300 { border-color: #d1d5db !important; }
    .border-gray-400 { border-color: #9ca3af !important; }
    .border-transparent { border-color: transparent !important; }

    .bg-\\[\\#171717\\] { background-color: #171717 !important; }
    .bg-white { background-color: #ffffff !important; }
    .bg-gray-50 { background-color: #f9fafb !important; }
    .bg-gray-100 { background-color: #f3f4f6 !important; }
    .bg-gray-200 { background-color: #e5e7eb !important; }
    .bg-green-600 { background-color: #16a34a !important; }

    .text-white { color: #ffffff !important; }
    .text-gray-400 { color: #9ca3af !important; }
    .text-gray-500 { color: #6b7280 !important; }
    .text-gray-600 { color: #4b5563 !important; }
    .text-gray-700 { color: #374151 !important; }
    .text-gray-900 { color: #111827 !important; }
    .text-red-500 { color: #ef4444 !important; }
    .text-pink-400 { color: #f472b6 !important; }
    .text-pink-500 { color: #ec4899 !important; }

    /* Force hover states */
    .hover\\:border-gray-300:hover { border-color: #d1d5db !important; }
    .hover\\:border-gray-400:hover { border-color: #9ca3af !important; }
    .hover\\:border-pink-400:hover { border-color: #f472b6 !important; }
    .hover\\:bg-\\[\\#2a2a2a\\]:hover { background-color: #2a2a2a !important; }
    .hover\\:bg-gray-100:hover { background-color: #f3f4f6 !important; }
    .hover\\:text-pink-500:hover { color: #ec4899 !important; }

    /* Force ring styles for wishlist hover */
    .hover\\:ring-2:hover {
      box-shadow: 0 0 0 2px #f472b6 !important;
    }
    .hover\\:ring-pink-400:hover {
      box-shadow: 0 0 0 2px #f472b6 !important;
    }

    /* Force transitions */
    .transition-colors {
      transition-property: color, background-color, border-color !important;
      transition-duration: 0.15s !important;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1) !important;
    }
    .transition-all {
      transition-property: all !important;
      transition-duration: 0.15s !important;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1) !important;
    }
    .transition-transform {
      transition-property: transform !important;
      transition-duration: 0.15s !important;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1) !important;
    }

    /* Force transforms for wishlist sidebar */
    .translate-x-0 { transform: translateX(0) !important; }
    .-translate-x-full { transform: translateX(-100%) !important; }
    .translate-x-\\[140px\\] { transform: translateX(140px) !important; }
    .-translate-y-1\\/2 { transform: translateY(-50%) !important; }

    /* Force fixed positioning */
    .fixed { position: fixed !important; }

    /* Force z-index - HIGH values to go above WordPress/Breakdance header */
    .z-30 { z-index: 99930 !important; }
    .z-40 { z-index: 99940 !important; }
    .z-50 { z-index: 99950 !important; }

    /* Swiper-specific forced styles */
    .swiper-root {
      /* ordinateur : hauteur restante sous l'en-tête, mesurée par fitSwiper() ; sinon les boutons sortaient de l'écran */
      height: var(--fc-swiper-h, 100dvh) !important;
    }
    @media (max-width: 1023px) {
      .swiper-root {
        height: calc(100dvh - 90px) !important;
      }
    }
    .swiper-card {
      position: absolute !important;
      inset: 0 !important;
    }
    .swiper-stack {
      touch-action: none !important;
    }
    .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1) !important; }
    .shadow-xl { box-shadow: 0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1) !important; }
    .shadow-2xl { box-shadow: 0 25px 50px -12px rgba(0,0,0,.25) !important; }

    /* Swiper color forces */
    .text-green-500 { color: #22c55e !important; }
    .text-yellow-500 { color: #eab308 !important; }
    .border-green-500 { border-color: #22c55e !important; }
    .border-red-500 { border-color: #ef4444 !important; }
    .border-yellow-500 { border-color: #eab308 !important; }
    .border-gray-100 { border-color: #f3f4f6 !important; }
    .bg-pink-500 { background-color: #ec4899 !important; }
    .bg-pink-100 { background-color: #fce7f3 !important; }
    .bg-black\\/30 { background-color: rgba(0,0,0,0.3) !important; }
    .bg-black\\/50 { background-color: rgba(0,0,0,0.5) !important; }
    .bg-white\\/80 { background-color: rgba(255,255,255,0.8) !important; }
    .hover\\:bg-black:hover { background-color: #000000 !important; }
    .hover\\:bg-red-50:hover { background-color: #fef2f2 !important; }
    .hover\\:text-red-500:hover { color: #ef4444 !important; }
    .hover\\:text-gray-600:hover { color: #4b5563 !important; }
  `
  shadowRoot.appendChild(styleEl)

  const renderContainer = document.createElement('div')
  shadowRoot.appendChild(renderContainer)

  return { shadowRoot, renderContainer }
}

function initSwiper(options: InitOptions) {
  const { container, funnelId, apiUrl } = options

  const baseApiUrl = apiUrl || DEFAULT_API_URL
  setWidgetApiBaseUrl(baseApiUrl)

  const containerEl = typeof container === 'string'
    ? document.querySelector(container)
    : container

  if (!containerEl) {
    console.error('[FunnelSwiper] Container not found:', container)
    return
  }

  if (!funnelId) {
    console.error('[FunnelSwiper] funnelId is required')
    return
  }

  const { renderContainer } = createShadowRoot(containerEl)

  const root = createRoot(renderContainer)
  root.render(
    <React.StrictMode>
      <Swiper slugs={funnelId} />
    </React.StrictMode>
  )
  fitSwiper(containerEl as HTMLElement)
}

/**
 * Ordinateur : le swipe prend la hauteur d'écran qui reste sous l'en-tête du site, pas tout l'écran. Avec 100dvh il
 * commençait ~140 px plus bas que le haut de la page et débordait de son bloc Breakdance (lui-même haut d'un écran) :
 * ses boutons tombaient sous l'écran, sous la bande suivante (« Livraison offerte… »). Recalculé au redimensionnement
 * et quand la mise en page bouge (polices, en-tête). Mobile : inchangé (règle CSS à 100dvh - 90px).
 */
function fitSwiper(host: HTMLElement) {
  const fit = () => {
    if (window.innerWidth < 1024) { host.style.removeProperty('--fc-swiper-h'); return }
    const top = host.getBoundingClientRect().top + window.scrollY
    host.style.setProperty('--fc-swiper-h', Math.max(460, Math.round(window.innerHeight - top - 16)) + 'px')
  }
  fit()
  window.addEventListener('resize', fit)
  window.addEventListener('load', fit)
  setTimeout(fit, 600)
  setTimeout(fit, 2000)
}

// Expose global API
window.FunnelConfigurator = {
  init,
  initSwiper,
  version: '2.1.0'
}

// Parcours « Compose ton lot » : [funnel_bundle_wizard ids="..."], plusieurs lots (un par taille).
// Le script est chargé en async sur cette page (vitesse, 31/10/2026) : le lot démarre dès son arrivée, sans attendre
// la fin de la page ; son conteneur est écrit juste avant le script, il existe déjà. Rappelé au DOMContentLoaded
// pour un éventuel conteneur plus bas (chacun n'est monté qu'une fois).
function mountWizards() {
  document.querySelectorAll('[data-funnel-wizard]').forEach(container => {
    const el = container as HTMLElement
    if (el.dataset.fcMounted) return
    const slugs = (el.dataset.funnelIds || '').split(',').map(s => s.trim()).filter(Boolean)
    if (!slugs.length || !el.dataset.apiUrl) return
    el.dataset.fcMounted = '1'
    let wcData = {}
    try { wcData = JSON.parse(el.dataset.wcData || '{}') } catch { /* données WooCommerce absentes : vignettes du configurateur */ }
    // Réglages des lots écrits dans la page par le plugin : pas d'aller-retour vers l'API
    let funnels: Record<string, unknown> | undefined
    try { const s = el.querySelector('script.fc-wizard-funnels'); if (s) funnels = JSON.parse(s.textContent || '{}') } catch { /* sinon : API */ }
    mountBundleWizard(el, {
      slugs,
      apiUrl: el.dataset.apiUrl,
      wcAjaxUrl: el.dataset.wcAjaxUrl,
      wcNonce: el.dataset.wcNonce,
      wcData,
      funnels,
      urls: { checkout: el.dataset.checkoutUrl || '/checkout/', shop: el.dataset.shopUrl || '/' },
      lang: el.dataset.lang, // langue TrueLang de la page (vide en français)
    })
  })
}
if (document.readyState !== 'loading' || document.querySelector('[data-funnel-wizard]')) mountWizards()

// Auto-init from data attributes (script en async possible : si la page est déjà prête, on démarre tout de suite)
const autoInit = () => {
  // Init configurators AND bundles (init() auto-dispatches based on funnel.type)
  const widgetSelector = '[data-funnel-configurator],[data-funnel-bundle]'
  document.querySelectorAll(widgetSelector).forEach(container => {
    const el = container as HTMLElement
    const funnelId = el.dataset.funnelId
    const apiUrl = el.dataset.apiUrl
    const wcAjaxUrl = el.dataset.wcAjaxUrl
    const wcNonce = el.dataset.wcNonce

    if (funnelId) {
      init({
        container: el,
        funnelId,
        apiUrl,
        wcAjaxUrl,
        wcNonce
      })
    }
  })

  mountWizards()

  // Init swipers
  document.querySelectorAll('[data-funnel-swiper]').forEach(container => {
    const el = container as HTMLElement
    const funnelId = el.dataset.funnelId
    const apiUrl = el.dataset.apiUrl

    if (funnelId) {
      initSwiper({
        container: el,
        funnelId,
        apiUrl,
      })
    }
  })
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit)
else autoInit()
